/**
 * Can THIS hand write THIS ticket — the field app's mirror of the API's rule.
 *
 * ─── Why a mirror exists at all ──────────────────────────────────────────────
 *
 * The API's `FIELD_TICKET_WRITE_PREDICATE` (fieldiq-api
 * `src/lib/ticket-access.ts`) is embedded in GET /employee/my-tickets/:id, PUT
 * /employee/my-tickets/:id, field-approve and ai-prefill. A ticket that fails it
 * answers **404 "Ticket not found or not yours"** — deliberately 404 and not
 * 403, so ticket ids cannot be probed by reading status codes.
 *
 * That is a good rule and a terrible sentence to show a field hand, because the
 * LIST route's predicate is much wider. GET /employee/my-tickets returns a
 * ticket if the user submitted it, OR is an assigned line-item tech on it, OR
 * **is on the job's crew at all**. So a hand can plainly see a ticket in a list
 * and be told by the next screen that it does not exist.
 *
 * This module exists so a control that is going to be refused is never offered,
 * in the same spirit as `ticketEditIsFrozen` in lib/ticket-status.ts. It is a
 * MIRROR, not the authority: the API refuses us regardless of what this file
 * says, and if the predicate there changes this file is stale until someone
 * updates it. Every call site must still handle the 404.
 *
 * ─── The rule, and why it is total rather than a guess ───────────────────────
 *
 * A field user may write a ticket iff:
 *
 *   1. they are the designated ticket writer on the ticket's job
 *      (`job_crew_assignments.is_ticket_writer`), or
 *   2. they submitted the ticket (`tickets.submitted_by`).
 *
 * Clause 2 is the API's deliberate fallback until a real `jobs.lead_user_id`
 * exists.
 *
 * ⚠️ CLAUSE 1 IS LIVE. This paragraph said "clause 1 currently matches nobody —
 * 0 of 331 rows across 5 tenants, checked 2026-08-27", which was true when
 * written and has been FALSE since 2026-08-28. Re-measured against production
 * 2026-09-10: `job_crew_assignments.is_ticket_writer` holds SIX rows — five at
 * TNT (Reece Turner J-4293, Colton Comeau J-4310, Jason Zintel J-4315, PieterJan
 * Hoogendoorn J-4322, Voon Ten J-4324) and one at Smith (Jim Smith J-0017),
 * every holder a `field_worker`, and J-4315 is still ACTIVE.
 *
 * So the `is_ticket_writer` key on the crew payload is not a provision for a
 * hypothetical future — it decides, today, whether five TNT hands see a Write
 * ticket control. The hard dependency stated at the bottom of this docblock is
 * therefore live too: an endpoint that stops emitting the key hides the control
 * from exactly those five, silently.
 *
 * Clause 1 was implemented before anyone used it, which was right: a rule that is
 * only correct because a column happens to be empty is not a rule. Keep the
 * separate tests for it in `field-ticket-write.test.ts` for the same reason —
 * authorship no longer covers for it, but it did for four months and nothing
 * about the code said when that stopped.
 *
 * There is NO unknown case to guess at, which is why this returns a plain
 * boolean rather than taking the "prefer the recoverable mistake" escape hatch
 * that lib/ticket-status.ts argues for:
 *
 *   - `submitted_by` is a real column, selected by `t.*` on every server row.
 *     A NULL there (an office-created ticket with no field submitter) is not
 *     missing information — it is the answer, and it matches the SQL, where
 *     `tickets.submitted_by = $3` is false for NULL.
 *   - A record still queued offline (`_pendingSync`) has no `submitted_by` yet
 *     because no server has assigned one. It is not unknown either: an offline
 *     create exists only on the device that made it, so its author is whoever
 *     is holding the phone. Treated as writable, which is also what the sync
 *     flush will make true.
 *
 * ─── IT USED TO READ THIS OFF `role_on_job`, AND THAT IS WORTH KNOWING ──────
 *
 * Until API 0342 the designation was the sentinel `role_on_job = 'ticket_writer'`
 * and this file compared against it. That worked, and the reasoning for why it
 * was safe was correct: `GET /employee/my-jobs/:id` returns
 * `COALESCE(jca2.role_on_job, u.role::text)`, and the fallback arm could not
 * produce the sentinel because `users.role` is a Postgres enum whose complete
 * value set is admin, office_staff, supervisor, field_worker, logistics,
 * dispatcher, customer.
 *
 * It was replaced anyway, because the sentinel was not free where it was
 * WRITTEN: three API readers COALESCE `role_on_job` over `users.specialty`, so
 * designating a Tong Technician hid his trade from the deterministic
 * line-item tech matcher and from the pay prompt. See the migration header.
 *
 * The consequence here is a hard dependency, stated so it is not rediscovered
 * by debugging: **this module reads `is_ticket_writer` off the crew payload,
 * and every endpoint that feeds it must emit that key.** As of 0342 those are
 * `GET /employee/my-jobs/:id`, `GET /jobs/:id`, `GET /dispatch/jobs` and
 * `PATCH /dispatch/jobs/:id/crew`. A payload missing the key does not error —
 * it reads as `undefined`, which is falsy, which means "not the writer" for
 * everyone. That renders as the Write ticket control being hidden from the one
 * hand who has it, silently. `?? false` on a field you believe exists is a
 * fallback on your belief, not on the data.
 */

/**
 * The `role_on_job` value that USED to mark the designated writer, kept only so
 * a client running against a pre-0342 API is still read correctly during the
 * deploy window. Nothing writes it any more.
 *
 * Delete it once the API has been on 0342 long enough that no cached bundle can
 * still be talking to the old one — it is a compatibility shim with an expiry,
 * not part of the model.
 */
export const LEGACY_TICKET_WRITER_ROLE = 'ticket_writer';

/** The fields of a ticket this module reads. Accepts either serialization. */
export interface WritableTicketShape {
  submitted_by?: string | null;
  submittedBy?: string | null;
  /**
   * The SERVER's answer to this exact question, on rows from
   * `GET /employee/my-tickets`. Prefer it over anything derived here.
   *
   * It exists because the list is wider than the write rule and its rows carry
   * no job crew — `crew` on a ticket is the closed-ticket snapshot and is null
   * on every draft, which is precisely the case that matters. Without this the
   * local computation can only see clause 2 (author) and would answer "no" for
   * a designated writer, hiding the control from the one hand who has it.
   */
  can_write?: boolean | null;
  canWrite?: boolean | null;
  /** Set by useOfflineMutation on a create that has not synced yet. */
  _pendingSync?: boolean;
}

/**
 * A crew member as GET /employee/my-jobs/:id returns one. `id` is the USER's
 * id (`'id', u.id` in the payload), not the assignment row's.
 */
export interface JobCrewMemberShape {
  id?: string | null;
  user_id?: string | null;
  userId?: string | null;
  role_on_job?: string | null;
  roleOnJob?: string | null;
  /** The designation (API 0342). Absent on a pre-0342 payload. */
  is_ticket_writer?: boolean | null;
  isTicketWriter?: boolean | null;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

function crewMemberUserId(member: JobCrewMemberShape): string | null {
  return member.id ?? member.user_id ?? member.userId ?? null;
}

/** The submitter's id, whichever way the payload spelled it. */
export function ticketSubmitterId(ticket: WritableTicketShape | null | undefined): string | null {
  if (!ticket) return null;
  return ticket.submitted_by ?? ticket.submittedBy ?? null;
}

/**
 * Clause 1: is this user the designated ticket writer on this job's crew?
 *
 * Returns false for an absent user id rather than matching a crew row whose
 * own id is also absent — two unknowns are not a match.
 */
export function isDesignatedTicketWriter(
  crew: JobCrewMemberShape[] | null | undefined,
  userId: string | null | undefined,
): boolean {
  if (!userId || !Array.isArray(crew)) return false;
  return crew.some((m) => {
    if (crewMemberUserId(m) !== userId) return false;
    // 0342's boolean is authoritative when the payload carries it. `=== true`
    // rather than truthiness so a `null` from a nullable column and an absent
    // key are not conflated with false by different routes — and note the
    // check is on the KEY being present, not on the value, because `false` is
    // a real answer and must not fall through to the legacy arm.
    const flag = m.is_ticket_writer ?? m.isTicketWriter;
    if (flag !== undefined && flag !== null) return flag === true;
    // Pre-0342 payload (a bundle talking to an API that has not deployed yet).
    return (m.role_on_job ?? m.roleOnJob) === LEGACY_TICKET_WRITER_ROLE;
  });
}

/**
 * The whole predicate: ON THE JOB'S CREW, or the ticket's author.
 *
 * Mirrors `FIELD_TICKET_WRITE_PREDICATE` in the API's src/lib/ticket-access.ts
 * and must agree with it. Clause 1 was `is_ticket_writer` until the write gate
 * went back to crew membership — August's rule, lost as collateral when
 * #435/#436 fixed a DELETE hole by pointing every field door at one predicate.
 *
 * Order matters and each step is load-bearing:
 *
 *   1. An unsynced local create is writable. Its author is whoever is holding
 *      the phone, and the sync flush will make that true server-side.
 *   2. If the payload carries the server's own `can_write`, that IS the answer.
 *      It is computed from the same rule the write doors enforce, so it cannot
 *      drift from them, and it knows things this function cannot — crew rows
 *      live in job_crew_assignments, which a ticket row does not carry.
 *   3. Otherwise fall back to computing it here, which needs the job crew and
 *      is what the job screen has.
 *
 * Step 2 is checked for KEY PRESENCE, not truthiness: `false` is a real answer
 * and must not fall through to step 3, where a crew check would overturn it.
 *
 * THE DESIGNATION IS NOT PART OF THIS ANY MORE and `isDesignatedTicketWriter`
 * is not called here. It has not been retired — it governs DELETE now — so the
 * helper stays, and a designated hand is admitted by this function for the
 * ordinary reason that he is on the crew.
 */
export function canWriteFieldTicket(
  ticket: WritableTicketShape | null | undefined,
  userId: string | null | undefined,
  crew: JobCrewMemberShape[] | null | undefined,
): boolean {
  if (!ticket) return false;
  if (ticket._pendingSync) return true;
  const server = ticket.can_write ?? ticket.canWrite;
  if (server !== undefined && server !== null) return server === true;
  if (isOnJobCrew(crew, userId)) return true;
  const submitter = ticketSubmitterId(ticket);
  return !!userId && !!submitter && submitter === userId;
}


/**
 * The sentence shown in place of an Open control on a ticket this hand cannot
 * write. Returns null when they CAN write it.
 *
 * ─── WHO IT NAMES, AND IN WHAT ORDER ────────────────────────────────────────
 *
 * The same order the API's `explainFieldTicketWriteRefusal` uses, because the
 * two sentences are read by the same person minutes apart and disagreeing about
 * who holds a ticket is worse than either one being vague:
 *
 *   1. the job's DESIGNATED writer, if the crew payload identifies one;
 *   2. otherwise the ticket's author, if they are on this job's crew;
 *   3. otherwise nobody — say less rather than guess.
 *
 * Order 1 before 2 matters now that designations are real. Under the sentinel
 * this function only ever had authorship to point at, and it said "started this
 * ticket" — which is simply false about a designated writer who did not start
 * it. A confidently wrong name sends a hand to the wrong person.
 *
 * A submitter who is not on the crew (the office started the ticket) gets the
 * unnamed form: resolving a uuid we were not given a name for would be worse
 * than saying less.
 *
 * It deliberately does NOT say "not found". That is the API's old wording, and
 * repeating it in front of a hand who is looking straight at the ticket is how
 * an access rule gets reported as a data-loss bug.
 */
export function ticketWriteRefusalReason(
  ticket: WritableTicketShape | null | undefined,
  userId: string | null | undefined,
  crew: JobCrewMemberShape[] | null | undefined,
): string | null {
  if (canWriteFieldTicket(ticket, userId, crew)) return null;

  /*
   * IT NO LONGER NAMES A HOLDER, AND THAT IS THE POINT RATHER THAN A LOSS.
   *
   * This used to name the job's designated writer, or the author, and say "only
   * they can add to it" — the right sentence when exactly one hand held a
   * ticket. Under crew-scoped write a ticket has no single holder: everyone on
   * the crew writes it. Naming one would be a confidently wrong answer, which
   * is the failure mode this function's docblock already warns about in the
   * other direction.
   *
   * The only way to be refused now is to be off the crew, so that is what it
   * says — and the action it asks for is the one that would actually help. It
   * matches the API's `explainFieldTicketWriteRefusal` word for word in
   * substance, because the two sentences are read by the same person minutes
   * apart.
   */
  return 'This ticket belongs to a job you are not on the crew of. Ask dispatch to add you to it.';
}

/**
 * Is this user on the job's crew at all?
 *
 * TWO CALLERS NOW, and the second one IS an access check: since the write gate
 * went back to crew membership this is clause 1 of `canWriteFieldTicket`, the
 * mirror of the API's FIELD_TICKET_WRITE_PREDICATE. A second copy was written
 * and deleted in the same commit — one definition, because a mirror that
 * disagrees with itself is worse than no mirror.
 *
 * Note what it does NOT read: `is_ticket_writer`, `role_on_job`, or anything
 * else on the row. Presence in the array IS the answer, which makes it robust
 * against a payload that omits the designation keys — the hazard the module
 * docblock describes, where an absent key reads as `undefined`, which is falsy,
 * which used to mean "not the writer" for everyone and hid the editor from the
 * one hand who had it.
 *
 * Its ORIGINAL caller is not an access check — it answers whether the
 * ['field-my-tickets'] list can be trusted as the COMPLETE set of tickets on a
 * job. That list admits a ticket
 * when the user submitted it, is an assigned line-item tech on it, or is on the
 * job's crew; only the third clause makes it exhaustive for a given job.
 *
 * Reachable because the two endpoints disagree about who may look. The API's
 * `enforceJobCrewMembership` 404s a `field_worker` who is not on the crew and
 * returns early for every other role, so an admin, dispatcher, supervisor or
 * office_staff can open a field job screen for a job they are not crewed on and
 * be handed a partial ticket list with nothing marking it partial.
 */
export function isOnJobCrew(
  crew: JobCrewMemberShape[] | null | undefined,
  userId: string | null | undefined,
): boolean {
  if (!userId || !Array.isArray(crew)) return false;
  return crew.some((m) => crewMemberUserId(m) === userId);
}

/**
 * Should a LIST row refuse to open its editor?
 *
 * Deliberately NOT `!canWriteFieldTicket(...)`, and the difference is the whole
 * point of this function existing.
 *
 * `canWriteFieldTicket` answers "may they write it", and where it cannot tell
 * it falls back to the authorship clause — which is a fine default on the job
 * screen, where the crew is in hand and clause 1 is genuinely evaluable. On a
 * LIST row it is not: the row carries no job crew, so an absent `can_write`
 * plus a hand who did not author the ticket produces `false` for a DESIGNATED
 * WRITER, and the control disappears for the one person who has it.
 *
 * Two situations produce that shape and neither is rare:
 *
 *   - the deploy window, when a cached bundle is talking to an API that has not
 *     shipped `can_write` yet;
 *   - any caller that renders a ticket object assembled somewhere else.
 *
 * So this refuses ONLY on a definite server "no". Anything unknown opens, and
 * the API answers — which is the recoverable mistake: a hand who opens an
 * editor they cannot save gets a 403 naming who holds it, whereas a hand whose
 * own ticket will not open has no way forward at all and no message either.
 *
 * `_pendingSync` is excluded because an unsynced local create has no server
 * answer to defer to, and its author is whoever is holding the phone.
 */
export function fieldTicketWriteRefused(
  ticket: WritableTicketShape | null | undefined,
): boolean {
  if (!ticket || ticket._pendingSync) return false;
  return (ticket.can_write ?? ticket.canWrite) === false;
}
